import { Controller } from '@hotwired/stimulus';
import { afterTransition } from '../lib/transition';

// Modale Bootstrap 3 : `.modal` affichée puis marquée
// `in` (transition CSS `.fade`), arrière-plan `.modal-backdrop` ajouté au
// body, défilement de la page bloqué par `body.modal-open` en compensant la
// largeur de la barre de défilement pour éviter le décalage de la page.
//
// Actions attendues : `modal#open` sur le déclencheur, `modal#close` sur les
// boutons de fermeture, `click->modal#closeOnBackdrop` sur la cible `dialog`
// (le `.modal` couvre le viewport : un clic hors de `.modal-dialog` le vise) et
// `keydown.esc@document->modal#close`.
export default class ModalController extends Controller<HTMLElement> {
  static targets = ['dialog'];

  declare readonly dialogTarget: HTMLElement;

  private trigger: HTMLElement | null = null;

  private backdrop: HTMLElement | null = null;

  private cancelClose: (() => void) | null = null;

  private cancelBackdropRemoval: (() => void) | null = null;

  // Détaché pendant l'ouverture ou le fondu de fermeture : rien ne
  // libérerait plus la page.
  disconnect(): void {
    this.cancelClose?.();
    this.cancelClose = null;
    this.cancelBackdropRemoval?.();
    this.cancelBackdropRemoval = null;
    this.backdrop?.remove();
    this.backdrop = null;
    unlockBody();
    this.trigger = null;
  }

  open(event: Event): void {
    event.preventDefault();

    if (this.isOpen) return;

    this.trigger = event.currentTarget as HTMLElement;

    // Réouverture pendant le fondu de fermeture.
    this.cancelClose?.();
    this.cancelClose = null;

    this.showBackdrop();
    lockBody();

    this.dialogTarget.style.display = 'block';
    reflow(this.dialogTarget);
    this.dialogTarget.classList.add('in');
    this.dialogTarget.setAttribute('aria-hidden', 'false');
    this.dialogTarget.focus();
  }

  close(): void {
    if (!this.isOpen) return;

    this.dialogTarget.classList.remove('in');
    this.dialogTarget.setAttribute('aria-hidden', 'true');

    this.cancelClose = afterTransition(this.dialogTarget, () => {
      this.cancelClose = null;
      this.dialogTarget.style.display = '';
      unlockBody();
      this.hideBackdrop();
      this.trigger?.focus();
      this.trigger = null;
    });
  }

  closeOnBackdrop(event: Event): void {
    if (event.target === this.dialogTarget) this.close();
  }

  private showBackdrop(): void {
    // Réouverture pendant le fondu du backdrop : on garde celui-là.
    this.cancelBackdropRemoval?.();
    this.cancelBackdropRemoval = null;

    if (!this.backdrop) {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'modal-backdrop fade';
      document.body.appendChild(this.backdrop);
      reflow(this.backdrop);
    }

    this.backdrop.classList.add('in');
  }

  private hideBackdrop(): void {
    const { backdrop } = this;

    if (!backdrop) return;

    backdrop.classList.remove('in');
    this.cancelBackdropRemoval = afterTransition(backdrop, () => {
      this.cancelBackdropRemoval = null;
      this.backdrop = null;
      backdrop.remove();
    });
  }

  private get isOpen(): boolean {
    return this.dialogTarget.classList.contains('in');
  }
}

function lockBody(): void {
  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;

  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  document.body.classList.add('modal-open');
}

function unlockBody(): void {
  document.body.classList.remove('modal-open');
  document.body.style.paddingRight = '';
}

// Force le navigateur à calculer les styles avant le changement de classe,
// sinon la transition d'apparition ne joue pas.
function reflow(element: HTMLElement): void {
  element.getBoundingClientRect();
}
